import { Burden } from '@deqo/foundation';
import React, { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { listen, tryAnd } from '../CodesThatsGottaGoSomewhereElse';
import { Point } from '../platform/geom/Point';

export type DragInitHandler<T> = (e: MouseEvent) => T;
export type DragHandler<T> = (state: T, e: MouseEvent) => boolean;

export const useDrag = <T extends unknown>(onInit: DragInitHandler<T>, onDrag: DragHandler<T>) => {

    const ticks = useRef(0);
    const [state, setState] = useState(null as T);
    const burdens = useRef([] as Burden[]);

    const onWindowMouseMove = useCallback((e: MouseEvent) => {
        if (!state) return;
        if (!onDrag(state, e)) {
            setState(null);
        }
    }, [state, onDrag]);

    const onWindowMouseUp = useCallback((e: MouseEvent) => {
        if (!state) return;
        if (e.button === 0) {
            tryAnd(() => onDrag(state, e));
            setState(null);
        }
    }, [state, onDrag]);

    useEffect(() => {
        const cancel = () => {
            Burden.resolveAll(burdens.current);
            burdens.current = [];
        };
        if (!!state) {
            cancel();
            burdens.current = [
                listen(window, 'mousemove', onWindowMouseMove),
                listen(window, 'mouseup', onWindowMouseUp),
            ];
        }
        return cancel;
    }, [state, onWindowMouseMove, onWindowMouseUp]);

    const onMouseDown = useCallback((e: React.MouseEvent<any, MouseEvent>) => {
        e.preventDefault();
        ticks.current = 0;
        setState(onInit(e.nativeEvent));
    }, [onInit]);

    return [onMouseDown, !!state] as const;
};

export type SimpleDragHandler = (vector: Point, from: Point, ticks: number, e: MouseEvent) => boolean;

export const useSimpleDrag = (handler: SimpleDragHandler) => {

    const ticks = useRef(0);
    const [from, setFrom] = useState(null as Point);
    const burdens = useRef([] as Burden[]);

    const onWindowMouseMove = useCallback((e: MouseEvent) => {
        if (!from) return;
        if (!handler(Point.vector(from, Point.from(e, 'client')), from, ticks.current++, e)) {
            setFrom(null);
        }
    }, [from, handler]);

    const onWindowMouseUp = useCallback((e: MouseEvent) => {
        if (!from) return;
        if (e.button === 0) {
            tryAnd(() => handler(Point.vector(from, Point.from(e, 'client')), from, ticks.current++, e));
            setFrom(null);
        }
    }, [from, handler]);

    useEffect(() => {
        const cancel = () => {
            Burden.resolveAll(burdens.current);
            burdens.current = [];
        };
        if (!!from) {
            cancel();
            burdens.current = [
                listen(window, 'mousemove', onWindowMouseMove),
                listen(window, 'mouseup', onWindowMouseUp),
            ];
        }
        return cancel;
    }, [from, onWindowMouseMove, onWindowMouseUp]);

    const onMouseDown = useCallback((e: React.MouseEvent<any, MouseEvent>) => {
        e.preventDefault();
        ticks.current = 0;
        if (handler(Point.zero, Point.from(e, 'client'), ticks.current++, e.nativeEvent)) {
            setFrom(Point.from(e, 'client'));
        }
    }, [handler]);

    return onMouseDown;
};

export const useMutationObserver = (
    ref: MutableRefObject<HTMLElement>,
    callback: MutationCallback,
    options = {
        attributes: true,
        characterData: true,
        childList: true,
        subtree: true,
    }
) => {
    useEffect(() => {
        if (ref.current) {
            const observer = new MutationObserver(callback);
            observer.observe(ref.current, options);
            return () => observer.disconnect();
        }
    }, [callback, options]);
};
